import fetchMiddleWare, { ApiHooks } from "@/shared/useCustomFetch";
import {
  asyncLocalStorage,
  generateApi,
  generatePayload,
  generateStoreGetters,
  storeDispatcher,
} from "@/shared/utils";
import { pathOr } from "ramda";
import store from "@/store";
import { getEnumAll, parvandehChangeRole } from "@/services";

const PRE_REGISTER_SESSION_KEY = "preRegisterSession";

const getPreRegisterSession = () => {
  try {
    const raw = sessionStorage.getItem(PRE_REGISTER_SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistPreRegisterSession = (partial: Record<string, any>) => {
  const current = getPreRegisterSession();
  sessionStorage.setItem(
    PRE_REGISTER_SESSION_KEY,
    JSON.stringify({ ...current, ...partial })
  );
};

const clearPreRegisterSession = () => {
  sessionStorage.removeItem(PRE_REGISTER_SESSION_KEY);
};

const hasPreRegisterCourse = (course: any) =>
  !!course && Object.keys(course).length > 0;

const isPreRegisterFlowReady = () => {
  const currentCourse = generateStoreGetters(["currentCourse"], "auth");
  const preRegisterCode = generateStoreGetters(["preRegisterCode"], "auth");

  return hasPreRegisterCourse(currentCourse) && !!preRegisterCode;
};

export const ensurePreRegisterFlow = async () => {
  if (isPreRegisterFlowReady()) return true;

  const session = getPreRegisterSession();

  if (hasPreRegisterCourse(session.currentCourse)) {
    await storeDispatcher("currentCourse", session.currentCourse, "auth");
  }
  if (session.preRegisterCode) {
    await storeDispatcher("preRegisterCode", session.preRegisterCode, "auth");
  }
  if (session.hasNationalCode !== undefined) {
    await storeDispatcher("hasNationalCode", session.hasNationalCode, "auth");
  }
  if (session.extraFields) {
    await storeDispatcher("extraFields", session.extraFields, "auth");
  }
  if (session.nationalCodePreview) {
    await storeDispatcher(
      "nationalCodePreview",
      session.nationalCodePreview,
      "auth"
    );
  }

  return isPreRegisterFlowReady();
};

interface PropsInterface {
  page?: number;
  immediate?: boolean;
  code?: string | string[] | null;
  nationalCode?: string;
}
type Hook = PropsInterface & ApiHooks;

export const getActiveRegistrations = (props: Hook) => {
  const queryString = { company_code: import.meta.env.VUE_APP_COMPANY_CODE };
  const url = generateApi(
    ["pre_register", "active_registrations"],
    [],
    queryString
  );

  const api = fetchMiddleWare({
    ...props,
    afterFetch({ data: response }) {
      const activeRegistrations = pathOr([], ["data"], response);
      return {
        data: {
          activeRegistrations: activeRegistrations.map((item) => {
            return {
              caption: pathOr("", ["link_caption"], item),
              code: pathOr(null, ["code"], item),
              registerCondition: pathOr("", ["register_condition"], item),
              gregorianStartDate: pathOr(
                null,
                ["start_date", "gregorian"],
                item
              ),
              jalaliStartDate: pathOr(null, ["start_date", "jalali"], item),
            };
          }),
          companyName: pathOr(
            "",
            ["configs", "other", "company_name"],
            response
          ),
        },
      };
    },
    url,
    method: "GET",
  });
  return api;
};

export const getDetailRegistrations = (props: Hook) => {
  const url = generateApi(
    ["pre_register", "detail_registration"],
    [props.code]
  );

  const api = fetchMiddleWare({
    ...props,
    async afterFetch({ data }) {
      await store.dispatch("auth/setAction", {
        key: "currentCourse",
        value: data,
      });
      persistPreRegisterSession({ currentCourse: data });
      return {
        data: {
          code: pathOr(null, ["code"], data),
          linkCaption: pathOr(null, ["link_caption"], data),
          linkRegisterCondition: pathOr(
            null,
            ["link_register_condition"],
            data
          ),
        },
      };
    },
    url,
    method: "GET",
  });
  return api;
};

export const sendMobile = (props: Hook) => {
  const url = generateApi(["pre_register", "send_mobile"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        code: "code",
        mobile: "mobile",
        captcha: "captcha",
        key: "key",
      });
    },
    async afterFetch({ data }) {
      const preRegisterCode = pathOr(null, ["pre_register_code"], data);

      await store.dispatch("auth/setAction", {
        key: "preRegisterCode",
        value: preRegisterCode,
      });
      persistPreRegisterSession({ preRegisterCode });

      return {
        data: {
          resExpireSeconds: pathOr(0, ["expire_seconds"], data),
          smsNumber: pathOr(null, ["sms_number"], data),
          expireAt: pathOr(null, ["expire_time"], data),
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PUT",
  });
};

export const resetData = async () => {
  await storeDispatcher("currentCourse", {}, "auth");
  await storeDispatcher("preRegisterCode", null, "auth");
  await storeDispatcher("hasNationalCode", false, "auth");
  await storeDispatcher("nationalCodePreview", null, "auth");
  await storeDispatcher("extraFields", null, "auth");
  await storeDispatcher("parvandehInfo", null, "auth");
  clearPreRegisterSession();
};

export const confirmMobile = (props: Hook) => {
  const url = generateApi(["pre_register", "confirm_mobile"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        otp_code: "OtpCode",
        pre_register_code: "preRegisterCode",
      });
    },
    async afterFetch({ data }) {
      const preRegisterCode = pathOr(null, ["pre_register_code"], data);
      const hasNationalCode = pathOr(false, ["has_national_code"], data);
      const extraFields = pathOr(null, ["extra_fields"], data);
      const parvandehInfo = pathOr(null, ["parvandeh_info"], data);
      const registerStatus = pathOr(null, ["register_status"], data);
      const dynamicFieldCaption = pathOr(null, ["register_code_caption"], data);

      await storeDispatcher("preRegisterCode", preRegisterCode, "auth");
      await storeDispatcher("hasNationalCode", hasNationalCode, "auth");
      await storeDispatcher("extraFields", extraFields, "auth");

      if (registerStatus === "register_done") {
        await resetData();
        await storeDispatcher("parvandehInfo", parvandehInfo, "auth");
      } else {
        persistPreRegisterSession({
          preRegisterCode,
          hasNationalCode,
          extraFields,
        });
      }

      return {
        data: {
          registerStatus,
          dynamicFieldCaption,
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PATCH",
  });
};

export const confirmRegistrationCode = (props: Hook) => {
  const url = generateApi(["pre_register", "confirm_registration_code"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        national_code: "nationalCode",
        code: "dynamicField",
        captcha: "captcha",
        key: "key",
        pre_register_code: "preRegisterCode",
      });
    },
    async afterFetch({ data }) {
      const preRegisterCode = pathOr(null, ["pre_register_code"], data);
      const parvandehInfo = pathOr(null, ["parvandeh_info"], data);
      const registerStatus = pathOr(null, ["register_status"], data);

      await storeDispatcher("preRegisterCode", preRegisterCode, "auth");

      if (registerStatus === "register_done") {
        await resetData();
        await storeDispatcher("parvandehInfo", parvandehInfo, "auth");
      } else {
        persistPreRegisterSession({ preRegisterCode });
      }

      return {
        data: {
          registerStatus,
          message: pathOr("", ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PUT",
  });
};

export const setPreRegister = (props: Hook) => {
  const url = generateApi(["pre_register", "pre_register"]);

  const { mutate: runGetEnumAll } = getEnumAll();
  const { mutate: runParvandehChangeRole } = parvandehChangeRole({
    immediate: false,
  });

  let routeName = "dashboard";

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(
        ctx,
        {
          national_code: "nationalCode",
          name: "name",
          family: "family",
          password: "password",
          birth_date: "birthDate",
          education_grade_id: "educationGrade",
          location_id: "sokonatCity",
          job_level_id: "jobLevel",
          job_category: "jobCategory",
          gender_id: "gender",
          marriage_id: "marriageId",
          birth_location_id: "birthLocationId",
          father_name: "fatherName",
          identify: "identify",
          religion_id: "religionId",
          allegiance_caption: "allegianceCaption",
          pre_register_code: "preRegisterCode",
          captcha: "captcha",
          key: "key",
          file: "file",
        },
        {
          grant_type: "password",
          scope: "*",
          client_id: import.meta.env.VUE_APP_CLIENT_ID,
          client_secret: import.meta.env.VUE_APP_CLIENT_SECRET,
          front_url: window.location.origin,
        }
      );
    },
    async afterFetch({ data }) {
      const activeParvandeh = pathOr(null, ["auth", "current_parvandeh"], data);
      const activeParvandehCode = pathOr(null, ["code"], activeParvandeh);
      const parvandehCount = pathOr(0, ["auth", "parvandeh_count"], data);
      const resourceName = pathOr(null, ["resource"], data);
      const resourceValue = pathOr(null, ["resource_id"], data);

      // set parvandeh information for use in result page
      await storeDispatcher("parvandehInfo", data, "auth");

      if (activeParvandeh) {
        clearPreRegisterSession();
        await asyncLocalStorage.setItem(
          "activeParvandeh",
          JSON.stringify(activeParvandeh)
        );
        await asyncLocalStorage.setItem(
          "parvandehCount",
          JSON.stringify(parvandehCount)
        );

        // get all enumeration when logged in
        await runGetEnumAll({});

        // set parvandeh change role
        const {
          data: { targetRoute },
        } = await runParvandehChangeRole({ code: activeParvandehCode });
        routeName = targetRoute;
      }

      return {
        data: {
          action: activeParvandehCode ? "LOGGED_IN" : "SHOW_RESULT",
          routeName,
          resourceName,
          resourceValue,
          message: pathOr("", ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};
